<template>
  <div @click.shift="editRessource()">
    <div v-if="!hideLabel" class="edit-field-label">{{ label }}</div>

    <v-row no-gutters align="center">
      <v-col cols="3" class="pr-2">
        <v-select
            ref="COUNTRY_SELECT"
            v-model="selectedCountryCode"
            :class="getEditFieldClass"
            :items="countryItems"
            item-title="label"
            item-value="iso"
            :disabled="disabled"
            :error="displayError"
            :hide-details="dataTable ? dataTable : false"
            :label="hideLabel ? label : undefined"
            variant="outlined"
            :readonly="readOnly"
            :single-line="true"
            density="default"
            @blur="onBlur()"
            @focus="onFocus()"
        >
          <template #item="{ props, item }">
            <v-list-item
                v-bind="{ ...props, title: undefined }"
            >

            <template #prepend>
                <span :class="item.raw.flagClass" class="country-flag"></span>
              </template>

              <v-list-item-title class="pl-2">
                {{ item.raw.label }} ({{ item.raw.callingCode }})
              </v-list-item-title>
            </v-list-item>
          </template>

          <template #selection="{ item }">
            <div class="country-selection">
              <span :class="item.raw.flagClass" class="country-flag"></span>
              <span class="pl-4">{{ item.raw.callingCode }}</span>
            </div>
          </template>
        </v-select>
      </v-col>

      <v-col cols="9" class="pa-0">
        <v-text-field
            ref="PHONE_INPUT"
            v-model="localNumber"
            :class="getEditFieldClass"
            :disabled="disabled"
            :error="displayError"
            :error-messages="displayError ? errorText : ''"
            :hide-details="dataTable ? dataTable : false"
            :label="hideLabel ? label : undefined"
            variant="outlined"
            :placeholder="placeholder"
            :readonly="readOnly"
            :ripple="false"
            :single-line="true"
            density="default"
            persistent-placeholder
            @blur="onBlur()"
            @focus="onPhoneFocus"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import {
  parsePhoneNumberFromString,
  type CountryCode
} from 'libphonenumber-js/max';
import { FieldError } from '@/framework/base/components/FieldInfo';
import { type EditRessource } from '@/framework/base/helpers/Ressources';
import { storeSession } from '@/generated/stores';
import UserProfile, { EnumProfile } from '@/framework/base/classes/UserProfile';
import { RULES_FRENCH_LABEL } from '@/generated/validators/ValidationRules';
import { getPhoneCountries, type PhoneCountry } from '@/framework/base/helpers/PhoneCountries';

export let cbEditRessource: EditRessource | undefined = undefined;

export function setEditRessource(editRessource: EditRessource) {
  cbEditRessource = editRessource;
}

type PhoneCountry = {
  iso: CountryCode;
  label: string;
};

export default defineComponent({
  name: 'EditPhoneField',
  props: {
    value: {
      type: String,
      required: true
    },
    contextId: {
      type: Number,
      required: true
    },
    required: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    readOnly: {
      type: Boolean
    },
    parentFocus: {
      type: Boolean
    },
    bold: {
      type: Boolean
    },
    hideLabel: {
      type: Boolean
    },
    dataTable: {
      type: Boolean
    },
    showMissing: {
      type: Boolean
    },
    overRidePlaceholder: {
      type: String
    },
    overRideLabel: {
      type: String,
      default: 'Téléphone mobile'
    },
    countryLabel: {
      type: String,
      default: 'Pays'
    },
    defaultCountry: {
      type: String as PropType<CountryCode>,
      default: 'FR'
    },
    countries: {
      type: Array as PropType<PhoneCountry[]>,
      default: () => getPhoneCountries('fr')
    },
    allowFixedLineOrMobile: {
      type: Boolean,
      default: true
    }
  },
  emits: ['updated', 'error', 'context'],
  data() {
    const showErrorTimeout = undefined as ReturnType<typeof setTimeout> | undefined;

    return {
      cbEditRessource,
      label: this.overRideLabel,
      selectedCountryCode: this.defaultCountry as CountryCode,
      localNumber: '',
      error: false,
      errorText: '',
      displayError: false,
      lostFocus: false,
      getFocus: false,
      editing: false,
      showErrorTimeout,
      lastContextId: 0,
      syncingFromParent: false
    };
  },
  computed: {
    getEditFieldClass(): string {
      let res = 'edit-field';
      if (this.bold) {
        res = 'edit-field-bold';
      }
      if (this.dataTable) {
        res += ' data-table-custom';
      }
      return res;
    },

    placeholder(): string {
      if (this.overRidePlaceholder) {
        return this.overRidePlaceholder;
      }
      return 'Téléphone mobile';
    },

    countryItems(): Array<PhoneCountry> {
      return this.countries;
    },

    normalizedValue(): string {
      const parsed = this.parseFromInput();
      return parsed ? parsed.number : '';
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value: string) {
        this.syncingFromParent = true;
        this.parseValue(value);
        this.syncingFromParent = false;
      }
    },

    localNumber() {
      if (this.syncingFromParent) return;
      this.editing = true;
      this.emitUpdated();
      this.checkError(false);
    },

    selectedCountryCode() {
      if (this.syncingFromParent) return;
      this.editing = true;
      this.emitUpdated();
      this.checkError(false);
    },

    showMissing(value: boolean) {
      if (value) {
        this.checkError(true);
      }
    }
  },
  methods: {
    parseValue(value: string) {
      if (!value) {
        this.localNumber = '';
        return;
      }

      const parsed = parsePhoneNumberFromString(value);

      if (parsed) {
        if (parsed.country) {
          this.selectedCountryCode = parsed.country as CountryCode;
        }
        this.localNumber = parsed.formatNational();
        return;
      }

      this.localNumber = value.replace(/[^\d]/g, '');
    },

    parseFromInput() {
      const raw = (this.localNumber || '').replace(/\D/g, '');
      if (!raw) {
        return undefined;
      }

      return parsePhoneNumberFromString(raw, this.selectedCountryCode);
    },

    emitUpdated() {
      this.$emit('updated', this.normalizedValue);
    },

    missing(): boolean {
      return !!this.required && (!this.normalizedValue || this.normalizedValue === '');
    },

    touch(): boolean {
      this.lostFocus = true;
      return this.checkError(true);
    },

    onBlur() {
      this.lostFocus = true;

      const parsed = this.parseFromInput();
      if (parsed && parsed.isValid()) {
        this.localNumber = parsed.formatNational();
      }

      if (!this.parentFocus) {
        this.checkError(true);
        this.editing = false;
      }
    },

    onFocus() {
      this.getFocus = true;
    },

    onPhoneFocus(event: FocusEvent) {
      this.onFocus();
      const target = event.target as HTMLInputElement | null;
      target?.select?.();
    },

    setFocus(): void {
      if (this.$refs.PHONE_INPUT) {
        // @ts-expect-error vuetify ref
        this.$refs.PHONE_INPUT.focus();
      }
    },

    validatePhone(): string {
      if (!this.required && !this.localNumber) {
        return '';
      }

      const raw = (this.localNumber || '').trim();
      if (!raw) {
        return this.required ? RULES_FRENCH_LABEL.text.required.error : '';
      }

      const parsed = this.parseFromInput();

      if (!parsed) {
        return 'Numéro invalide';
      }

      if (!parsed.isValid()) {
        return 'Numéro invalide';
      }

      const phoneType = parsed.getType();

      if (phoneType === 'MOBILE') {
        return '';
      }

      if (this.allowFixedLineOrMobile && phoneType === 'FIXED_LINE_OR_MOBILE') {
        return '';
      }

      return 'Le numéro doit être un numéro mobile';
    },

    checkError(displayError: boolean): boolean {
      let error = false;
      let errorLabel = '';

      clearTimeout(this.showErrorTimeout);

      if (this.contextId !== this.lastContextId) {
        this.lastContextId = this.contextId;
        this.$emit('context', this.value);
      }

      if (!this.normalizedValue || this.normalizedValue === '') {
        if (this.required && displayError) {
          error = true;
          errorLabel = this.label + ' (' + RULES_FRENCH_LABEL.text.required.error + ')';
        }
      } else {
        const validationError = this.validatePhone();
        if (validationError !== '') {
          error = true;
          errorLabel = validationError;
        }
      }

      if (error && !displayError) {
        this.displayError = false;
        this.showErrorTimeout = setTimeout(() => this.checkError(true), 1500);
      } else if (error && displayError) {
        this.errorText = errorLabel;
        this.displayError = true;
      } else {
        this.errorText = this.label;
        this.displayError = false;
      }

      if (error !== this.error) {
        this.error = error;
        this.$emit('error', new FieldError(0, '', this.error));
      }

      return error;
    },

    editRessource() {
      const user = storeSession.connectedUser;
      if (user) {
        const isAdmin = UserProfile.hasProfile(user, EnumProfile.admin);
        if (isAdmin && cbEditRessource) {
          cbEditRessource({ label: this.label } as any, this.updateRessource);
        }
      }
    },

    updateRessource() {
      // no-op
    }
  }
});
</script>

<style scoped>
:deep(.v-select .v-field) {
  min-height: 36px !important;
  height: 36px !important;
}

:deep(.v-select .v-field__input) {
  min-height: 36px !important;
  height: 36px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

:deep(.v-select .v-select__selection) {
  line-height: 36px;
}

:deep(.v-select .v-field__append-inner) {
  align-items: center;
  padding-top: 0 !important;
}



</style>